import React from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import WebIcon from './WebIcon';
import styles from './ItineraryEditor.styles';
import { mergePayload, normalizeExternalLink, buildMetaPairs, coalesceText, detectYouTubeEmbed, deriveImageUrl } from './itineraryUtils';
import OpeningHoursRow from '@cubemeet/ui/components/travel/OpeningHoursRow';

interface Props {
  it: any;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  onOpenPoi?: (payload: any) => void;
  dayDate?: string;
  onImageClick?: (images: any[], startIndex: number) => void;
  /** 是否在遊記上下文中渲染（不顯示時間，顯示地址/電話等） */
  isTravelPost?: boolean;
}

type AffiliatePartner = 'klook' | 'kkday' | 'trip' | 'booking' | 'agoda';

const PARTNER_LABELS: Record<AffiliatePartner, string> = {
  klook: 'Klook',
  kkday: 'KKday',
  trip: 'Trip.com',
  booking: 'Booking.com',
  agoda: 'Agoda',
};

const firstString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
    }
  }
  return undefined;
};

const detectPartner = (
  declared?: string | null,
  website?: string | null,
  siteName?: string | null,
): AffiliatePartner | null => {
  const normalizedDeclared = declared ? String(declared).trim().toLowerCase() : '';
  const normalizedSite = siteName ? siteName.trim().toLowerCase() : '';
  const normalizedHost = (() => {
    if (!website) return '';
    try {
      const href = website.startsWith('http') ? website : `https://${website}`;
      return new URL(href).hostname.trim().toLowerCase();
    } catch {
      return '';
    }
  })();
  const haystack = [normalizedDeclared, normalizedSite, normalizedHost].filter(Boolean);
  const includes = (keyword: string) => haystack.some((value) => value.includes(keyword));
  if (includes('klook')) return 'klook';
  if (includes('kkday')) return 'kkday';
  if (includes('trip.com') || includes('ctrip')) return 'trip';
  if (includes('booking')) return 'booking';
  if (includes('agoda')) return 'agoda';
  return null;
};

const ensureFavicon = (favicon?: string | null, website?: string | null): string | null => {
  if (favicon && typeof favicon === 'string' && favicon.trim()) return favicon.trim();
  if (!website) return null;
  try {
    const href = website.startsWith('http') ? website : `https://${website}`;
    const host = new URL(href).hostname;
    if (!host) return null;
    return `https://www.google.com/s2/favicons?domain=${host}&sz=64`;
  } catch {
    return null;
  }
};

const sanitizeTitle = (
  rawTitle?: string | null,
  contexts: Array<string | undefined | null> = [],
): string | undefined => {
  if (typeof rawTitle !== 'string') return undefined;
  let result = rawTitle.trim();
  if (!result) return undefined;
  const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const normalizedContexts = contexts
    .map((ctx) => (typeof ctx === 'string' ? ctx.trim() : ''))
    .filter((ctx) => ctx.length > 0);
  normalizedContexts.forEach((ctx) => {
    const escaped = escapeRegExp(ctx);
    const leadingPattern = new RegExp(`^${escaped}\\s*[\-–—|｜:：•‧]+\\s*`, 'i');
    const trailingPattern = new RegExp(`\\s*[\-–—|｜:：•‧]+\\s*${escaped}$`, 'i');
    const singlePattern = new RegExp(`^${escaped}$`, 'i');
    result = result.replace(leadingPattern, '').replace(trailingPattern, '').replace(singlePattern, '').trim();
  });
  result = result.replace(/^[\s\-–—|｜:：•‧]+/, '').replace(/[\s\-–—|｜:：•‧]+$/, '').trim();
  result = result.replace(/\s+/g, ' ').trim();
  if (!result) {
    return normalizedContexts[0] || rawTitle.trim();
  }
  // If title contains a dash/pipe-like delimiter, remove the suffix after the first delimiter
  // but only when the suffix looks like a site name or is short (to avoid chopping legit long titles).
  try {
    const parts = result.split(/[-–—|｜]/).map((s) => s.trim()).filter(Boolean);
    if (parts.length > 1) {
      const left = parts[0];
      const right = parts.slice(1).join(' ');
      const isShort = right.length <= 40;
      const hasDot = /\./.test(right);
      const matchesPartner = Object.values(PARTNER_LABELS).some((lbl) => right.toLowerCase().includes(lbl.toLowerCase()));
      if (isShort || hasDot || matchesPartner) {
        result = left;
      }
    }
  } catch {}
  return result;
};

const parseAdditionalParams = (value: unknown): Record<string, string> => {
  if (!value) return {};
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return {};
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object') return parseAdditionalParams(parsed);
    } catch {
      const params = new URLSearchParams(trimmed.startsWith('?') ? trimmed.slice(1) : trimmed);
      const result: Record<string, string> = {};
      params.forEach((v, k) => {
        if (v) result[k] = v;
      });
      return result;
    }
  }
  if (typeof value === 'object') {
    const result: Record<string, string> = {};
    Object.entries(value as Record<string, unknown>).forEach(([key, val]) => {
      if (typeof val === 'string') {
        const trimmed = val.trim();
        if (trimmed) result[key] = trimmed;
      } else if (typeof val === 'number' && Number.isFinite(val)) {
        result[key] = String(val);
      }
    });
    return result;
  }
  return {};
};

interface AffiliateUrlOptions {
  partner?: AffiliatePartner | null;
  affiliateAid?: string;
  affiliateCid?: string;
  affiliateAdid?: string;
  affiliateProd?: string;
  affiliateSid?: string;
  affiliateParamKey?: string;
  additionalParams?: Record<string, string>;
}

const DEFAULT_PARAM_BY_PARTNER: Record<string, string> = {
  klook: 'aid',
  kkday: 'cid',
  trip: 'allianceid',
  booking: 'aid',
  agoda: 'cid',
};

const buildAffiliateUrl = (
  url?: string | null,
  options: AffiliateUrlOptions = {},
): { url: string | undefined; appended: boolean } => {
  if (!url) return { url: undefined, appended: false };
  try {
    const parsed = new URL(url);
    let appended = false;
    const setParam = (key: string, value?: string, overrideExisting = false) => {
      if (!value) return;
      const existing = parsed.searchParams.get(key);
      if (existing && !overrideExisting) return;
      if (existing === value) return;
      parsed.searchParams.set(key, value);
      appended = true;
    };

    const {
      partner,
      affiliateAid,
      affiliateCid,
      affiliateAdid,
      affiliateProd,
      affiliateSid,
      affiliateParamKey,
      additionalParams,
    } = options;

    if (additionalParams) {
      Object.entries(additionalParams).forEach(([key, value]) => setParam(key, value));
    }

    setParam('cid', affiliateCid);
    setParam('adid', affiliateAdid);
    setParam('prod', affiliateProd);
    setParam('sid', affiliateSid);

    const normalizedPartner = partner ? String(partner).toLowerCase() : undefined;
    const paramKey = affiliateParamKey || (normalizedPartner ? DEFAULT_PARAM_BY_PARTNER[normalizedPartner] : undefined);
    if (affiliateAid) {
      if (paramKey) setParam(paramKey, affiliateAid);
      else setParam('aid', affiliateAid);
      if (normalizedPartner === 'trip') {
        setParam('aid', affiliateAid);
        setParam('allianceid', affiliateAid);
      }
    }

    const finalUrl = appended ? parsed.toString() : url;
    return { url: finalUrl, appended };
  } catch {
    return { url, appended: false };
  }
};

const deriveSiteDisplayName = (
  partner: AffiliatePartner | null,
  siteName?: string | null,
  host?: string | null,
): string | undefined => {
  if (typeof siteName === 'string' && siteName.trim()) return siteName.trim();
  if (partner && PARTNER_LABELS[partner]) return PARTNER_LABELS[partner];
  if (host) return host.replace(/^www\./, '');
  return undefined;
};

// Rich renderer for external shared webpages pasted or received via Web Share Target.
// Displays title, description, partner badge, preview image (if any) and a
// call-to-action button with affiliate tracking when available.
const ExternalAdapter: React.FC<Props> = ({ it, isExpanded = false, onToggleExpand, onOpenPoi, onImageClick, dayDate }) => {
  const merged = mergePayload(it) || {};
  // Development debug: log incoming merged payload and embed info when running locally
  try {
    if (typeof window !== 'undefined' && /localhost|127\.0\.0\.1/.test(window.location.host)) {
      // Keep this lightweight and readable
      console.log('[ExternalAdapter] render', {
        id: it?.id || null,
        title: it?.title || merged.title || null,
        hasEmbed: Boolean(merged && (merged.embed || (it && (it.payload || it.custom_payload) && ((it.payload && it.payload.embed) || (it.custom_payload && it.custom_payload.embed))))),
        embed: (merged && merged.embed) || (it && (it.payload || it.custom_payload) && ((it.payload && it.payload.embed) || (it.custom_payload && it.custom_payload.embed))) || null,
      });
    }
  } catch (e) {
    // ignore logging errors
  }
  const isGenericTitle = (t?: string) => !t || !t.trim() || ['新景点', '新條目', '行程条目', '条目', 'Item', 'New Item'].includes(t.trim());
  const rawTitle = (!isGenericTitle(it?.title) ? it.title : undefined) || merged.title || '嵌入內容';
  const description = firstString(it?.note, merged.description, it?.description) || '';
  const trimmedDescription = description.trim();
  const websiteCandidate = firstString(
    merged.resolvedFinalUrl,
    merged.resolvedUrl,
    merged.resolved_url,
    merged.website,
    merged.url,
    it?.website,
  );
  const fallbackWebsiteCandidate = firstString(merged.website, merged.url, it?.website);
  const normalizedWebsite = websiteCandidate
    ? normalizeExternalLink(websiteCandidate)
    : (fallbackWebsiteCandidate ? normalizeExternalLink(fallbackWebsiteCandidate) : undefined);
  const website = normalizedWebsite;
  const host = (() => {
    if (!website) return undefined;
    try { return new URL(website).hostname; } catch { return undefined; }
  })();
  const partner = detectPartner(merged.partner, website || fallbackWebsiteCandidate, merged.siteName);
  
  // First check if there's an embed object - if so, prioritize embed poster
  let embedObj = (merged && merged.embed) || null;

  // Fallback: if no embed object but we have a website URL, try to detect if it's a YouTube video
  if (!embedObj && (website || fallbackWebsiteCandidate)) {
    embedObj = detectYouTubeEmbed(website || fallbackWebsiteCandidate);
  }

  let embedPoster: string | undefined;
  try {
    if (embedObj && typeof embedObj === 'object') {
      if (embedObj.poster && typeof embedObj.poster === 'string') embedPoster = embedObj.poster;
      else if (embedObj.provider === 'youtube' && (embedObj.id || embedObj.src)) {
        const id = embedObj.id || (typeof embedObj.src === 'string' ? (embedObj.src.match(/embed\/([A-Za-z0-9_-]{6,})/) || [])[1] : undefined);
        if (id) embedPoster = `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
      }
    }
  } catch (e) {
    // ignore
  }
  
  // Prefer a derived image from the card/item itself (card-level fields only).
  // IMPORTANT: pass an empty mergedPayload to `deriveImageUrl` so it won't
  // scan `merged`/payload/inline/custom fields (which may include notes images).
  const hostImage = deriveImageUrl(it, {}) || undefined;
  // Pick only direct string fields from merged payload to avoid traversing
  // arrays/objects (e.g. notes) which may contain images that shouldn't be used
  // as the external link cover.
  const pickDirectString = (...vals: any[]) => {
    for (const v of vals) {
      if (typeof v === 'string') {
        const t = v.trim();
        if (t) return t;
      }
    }
    return undefined;
  };

  const payloadImage = pickDirectString(
    merged.imageUrl,
    merged.image,
    merged.image_url,
    merged.cover_image,
    merged.photo,
    merged.thumbnail,
    // embed poster fields if directly provided as strings
    merged.embed && typeof merged.embed.poster === 'string' && merged.embed.poster,
    merged.custom_payload && merged.custom_payload.embed && typeof merged.custom_payload.embed.poster === 'string' && merged.custom_payload.embed.poster,
    merged.payload && merged.payload.embed && typeof merged.payload.embed.poster === 'string' && merged.payload.embed.poster,
  );

  // Prefer an explicit image provided in custom_payload (strong guarantee)
  // Read explicit custom payload image only from the original item object `it`.
  const customPayloadImage = pickDirectString(
    it?.custom_payload && typeof it.custom_payload.imageUrl === 'string' && it.custom_payload.imageUrl,
    it?.custom_payload && typeof it.custom_payload.image_url === 'string' && it.custom_payload.image_url,
    it?.custom_payload && typeof it.custom_payload.image === 'string' && it.custom_payload.image,
  );

  // Also check for image provided in card.base_payload or merged payload (explicit external image)
  const basePayloadImage = pickDirectString(
    it?.card && it.card.base_payload && typeof it.card.base_payload.imageUrl === 'string' && it.card.base_payload.imageUrl,
    it?.card && it.card.base_payload && typeof it.card.base_payload.image_url === 'string' && it.card.base_payload.image_url,
    merged && typeof merged.imageUrl === 'string' && merged.imageUrl,
    merged && typeof merged.image_url === 'string' && merged.image_url,
  );

  // For embed items, prioritize embed poster; otherwise prefer payload-provided images
  // (including custom_payload.imageUrl) before falling back to card-level hostImage.
  // Final image priority (strict):
  // 1) embed poster (video embeds)
  // 2) explicit `it.custom_payload.imageUrl`
  // 3) explicit external image from card.base_payload or merged payload (e.g. base_payload.imageUrl)
  // 4) derived card-level image from `it` only (no payload scanning)
  // If this item is an external-type (payload.type === 'external' or card_type === 'external'),
  // we must NOT fallback to card-level cover/gallery images (these often come from notes/uploads).
  const isExternalItem = (merged && (merged.type === 'external' || merged.card_type === 'external')) ||
    (it && it.card && it.card.card_type === 'external');

  const finalImageUrl = embedPoster || customPayloadImage || basePayloadImage || (isExternalItem ? null : hostImage) || null;
  
  // Determine the site icon/favicon and siteName (must be before cleanedTitle)
  if (typeof window !== 'undefined' && /localhost|127\.0\.0\.1/.test(window.location.host)) {
    try {
      console.log('@@EXTERNAL-ADAPTER-FINAL_IMAGE@@', { id: it?.id || it?.card?.id, finalImageUrl, embedPoster, customPayloadImage, basePayloadImage, hostImage, isExternalItem });
    } catch (e) {}
  }
  let favicon = ensureFavicon(merged.favicon, website || fallbackWebsiteCandidate);
  
  // If no favicon found yet, try to extract from embed src
  if (!favicon && embedObj && embedObj.src && typeof embedObj.src === 'string') {
    try {
      const srcUrl = embedObj.src.startsWith('http') ? embedObj.src : `https://${embedObj.src}`;
      const srcHost = new URL(srcUrl).hostname;
      if (srcHost) {
        favicon = `https://www.google.com/s2/favicons?domain=${srcHost}&sz=64`;
      }
    } catch (e) {
      // ignore
    }
  }

  let siteName = merged.siteName;
  
  // For video embeds, detect platform from iframe src and set icon accordingly
  if (embedObj && embedObj.src && typeof embedObj.src === 'string') {
    const src = embedObj.src.toLowerCase();
    
    // Detect YouTube
    if (src.includes('youtube.com') || src.includes('youtu.be')) {
      if (!siteName) siteName = 'YouTube';
      if (!favicon) {
        favicon = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCI+PHBhdGggZmlsbD0iI0ZGMDAwMCIgZD0iTTIzLjQ5OCA2LjE4NmEzLjAxNiAzLjAxNiAwIDAgMC0yLjEyMi0yLjEzNkMxOS41MDUgMy41NDUgMTIgMy41NDUgMTIgMy41NDVzLTcuNTA1IDAtOS4zNzcuNTA1QTMuMDE3IDMuMDE3IDAgMCAwIC41MDIgNi4xODZDMCA4LjA3IDAgMTIgMCAxMnMwIDMuOTMuNTAyIDUuODE0YTMuMDE2IDMuMDE2IDAgMCAwIDIuMTIyIDIuMTM2YzEuODcxLjUwNSA5LjM3Ni41MDUgOS4zNzYuNTA1czcuNTA1IDAgOS4zNzctLjUwNWEzLjAxNSAzLjAxNSAwIDAgMCAyLjEyMi0yLjEzNkMyNCAzOS45MyAyNCAxMiAyNCAxMnMwLTMuOTMtLjUwMi01LjgxNHoiLz48cGF0aCBmaWxsPSIjRkZGIiBkPSJNOS41NDUgMTUuNTY4VjguNDMyTDE1Ljg2OCAxMnoiLz48L3N2Zz4=';
      }
    }
    // Detect Bilibili
    else if (src.includes('bilibili.com')) {
      if (!siteName) siteName = 'Bilibili';
      if (!favicon) {
        // Use local Bilibili icon from public assets
        favicon = '/assets/images/bilibili.png';
      }
    }
    // Detect Google Maps
    else if (src.includes('google.com/maps') || src.includes('maps.google.com')) {
      if (!siteName) siteName = 'Google Maps';
      if (!favicon) {
        // Use a reliable Google Maps icon from a public CDN or local asset
        favicon = 'https://upload.wikimedia.org/wikipedia/commons/a/aa/Google_Maps_icon_%282020%29.svg';
      }
    }
  }
  
  const siteDisplayName = siteName || deriveSiteDisplayName(partner, merged.siteName, host);
  const cleanedTitle = sanitizeTitle(rawTitle, [siteDisplayName, host]) || rawTitle;
  
  // Debug: log favicon resolution
  if (typeof window !== 'undefined' && /localhost|127\.0\.0\.1/.test(window.location.host)) {
    console.log('[ExternalAdapter] favicon debug:', {
      mergedFavicon: merged.favicon,
      website,
      fallbackWebsiteCandidate,
      finalFavicon: favicon,
      siteName: siteName,
      mergedSiteName: merged.siteName,
      siteDisplayName,
      hasEmbed: Boolean(embedObj && embedObj.src),
      embedSrc: embedObj?.src,
    });
  }

  const runtimeAid = typeof window !== 'undefined'
    ? firstString(
        (window as any).__TRIP_AID,
        (window as any).__KLOOK_AID,
        (window as any).__KKDAY_AID,
        (window as any).__AFFILIATE_AID,
        (window as any).__AFFILIATE_ID,
      )
    : undefined;

  const affiliateAid = firstString(
    merged.affiliateAid,
    merged.affiliate_id,
    merged.aid,
    merged.affiliate?.aid,
    merged.affiliate?.affiliateId,
    merged.meta?.affiliateAid,
    it?.meta?.affiliateAid,
    runtimeAid,
  );
  const affiliateCid = firstString(
    merged.affiliateCid,
    merged.cid,
    merged.analytics?.cid,
    merged.affiliate?.cid,
    merged.meta?.affiliateCid,
    it?.meta?.affiliateCid,
  );
  const affiliateAdid = firstString(
    merged.affiliateAdid,
    merged.adid,
    merged.analytics?.adid,
    merged.affiliate?.adid,
    merged.meta?.affiliateAdid,
    it?.meta?.affiliateAdid,
  );
  const affiliateProd = firstString(
    merged.affiliateProd,
    merged.prod,
    merged.analytics?.prod,
    merged.affiliate?.prod,
    merged.meta?.affiliateProd,
    it?.meta?.affiliateProd,
  );
  const affiliateSid = firstString(
    merged.affiliateSid,
    merged.sid,
    merged.analytics?.sid,
    merged.affiliate?.sid,
    merged.meta?.affiliateSid,
    it?.meta?.affiliateSid,
  );
  const affiliateParamKey = firstString(
    merged.affiliateParam,
    merged.affiliate_param,
    merged.affiliate?.param,
    merged.affiliate?.paramKey,
  );

  const additionalParams = {
    ...parseAdditionalParams(merged.affiliateParams),
    ...parseAdditionalParams(merged.affiliate_params),
    ...parseAdditionalParams(merged.analytics),
    ...parseAdditionalParams(merged.trackingParams),
    ...parseAdditionalParams(merged.tracking_params),
  };

  const { url: decoratedUrl } = buildAffiliateUrl(website, {
    partner,
    affiliateAid,
    affiliateCid,
    affiliateAdid,
    affiliateProd,
    affiliateSid,
    affiliateParamKey,
    additionalParams,
  });
  
  // 如果有 embed（視頻），完全禁用 finalWebsite，確保不會跳轉
  const finalWebsite = (embedObj?.src || embedObj?.html) ? undefined : (decoratedUrl || (website || (fallbackWebsiteCandidate ? normalizeExternalLink(fallbackWebsiteCandidate) : undefined)));

  const hasExpandable = trimmedDescription.length > 0;
  const isDegraded = typeof merged.degraded === 'boolean' ? merged.degraded : Boolean(it?.meta?.degraded);
  
  // Build metaPairs like other adapters do
  const metaPairs = buildMetaPairs(it, merged);
  
  // Extract specific fields from metaPairs or payload
  const addressFromMeta = metaPairs.find(p => {
    if (!p || !p.label) return false;
    const label = String(p.label).trim();
    return label === '地址' || label === 'Address';
  });
  const address = addressFromMeta?.value || coalesceText(it?.address, merged.address);
  
  const phoneFromMeta = metaPairs.find(p => {
    if (!p || !p.label) return false;
    const label = String(p.label).trim();
    return label === '電話' || label === '电话' || label === 'Phone';
  });
  const phone = phoneFromMeta?.value || coalesceText(it?.phone, merged.phone);
  
  const transportNote = (() => {
    const entry = metaPairs.find((pair) => {
      if (!pair || !pair.label) return false;
      const label = String(pair.label).trim();
      return label === '交通方式' || label === '交通';
    });
    if (!entry || !entry.value) return undefined;
    return String(entry.value).trim() || undefined;
  })();
  
  // Filter out metaPairs that should NOT be displayed:
  // - Already shown as dedicated fields (address, phone, transport)
  // - Metadata fields (coordinates, location name, activity type, type, etc.)
  const visibleMetaPairs = metaPairs.filter((p) => {
    if (!p || !p.label) return false;
    const label = String(p.label).trim();
    // Skip fields shown separately
    if (label === '地址' || label === 'Address') return false;
    if (label === '電話' || label === '电话' || label === 'Phone') return false;
    if (label === '交通方式' || label === '交通') return false;
    // Skip website URL as it is often too long and the card itself is clickable
    if (label === '网站' || label === '網站' || label === 'Website') return false;
    // Skip metadata/system fields that shouldn't be shown to users
    if (label === '坐标' || label === '座標' || label === 'Coordinates') return false;
    if (label === '地点' || label === '地點' || label === 'Location' || label === 'Place') return false;
    if (label === '活动类型' || label === '活動類型' || label === 'Activity Type') return false;
    if (label === '类型' || label === '類型' || label === 'Type') return false;
    if (label === 'type' || label === 'kind') return false;
    return true;
  });

  const renderSiteBadge = (variant: 'overlay' | 'inline') => {
    if (!siteDisplayName) return null;
    const background = variant === 'overlay' ? 'rgba(17,24,39,0.78)' : '#F3F4F6';
    const textColor = variant === 'overlay' ? '#FFFFFF' : '#1F2937';
    const iconColor = variant === 'overlay' ? '#F9FAFB' : '#6B7280';
    return (
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          padding: '6px 10px',
          borderRadius: '999px',
          background,
          color: textColor,
          fontSize: 12,
          fontWeight: 600,
          boxShadow: variant === 'overlay' ? '0 8px 20px rgba(15,23,42,0.28)' : 'none',
        }}
      >
        {favicon ? (
          <img
            src={favicon}
            alt={`${siteDisplayName} favicon`}
            style={{ width: 16, height: 16, borderRadius: 4, objectFit: 'cover', marginRight: 6 }}
            loading="lazy"
          />
        ) : (
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginRight: 6 }}>
            <WebIcon name="language" size={14} color={iconColor} />
          </span>
        )}
        <span style={{ whiteSpace: 'nowrap' }}>{siteDisplayName}</span>
      </div>
    );
  };

  const overlaySiteBadge = renderSiteBadge('overlay');
  const inlineSiteBadge = renderSiteBadge('inline');
  const handleOpen = () => {
    if (!finalWebsite) {
      if (hasExpandable && typeof onToggleExpand === 'function') onToggleExpand();
      return;
    }
    try {
      if (typeof window !== 'undefined') window.open(finalWebsite, '_blank', 'noopener,noreferrer');
    } catch {}
  };

  const handleToggleExpand = (event?: any) => {
    // 阻止事件冒泡，避免觸發卡片的點擊事件（打開詳情頁）
    if (event) {
      if (typeof event.stopPropagation === 'function') event.stopPropagation();
      if (event.nativeEvent && typeof event.nativeEvent.stopImmediatePropagation === 'function') {
        event.nativeEvent.stopImmediatePropagation();
      }
    }
    if (typeof onToggleExpand === 'function') onToggleExpand();
  };

  const shouldShowToggle = (() => {
    if (!trimmedDescription) return false;
    try {
      const len = trimmedDescription.length;
      if (len >= 80) return true;
      if (trimmedDescription.includes('\n')) return true;
      if ((trimmedDescription.match(/\s+/g) || []).length > 12) return true;
      return false;
    } catch {
      return false;
    }
  })();

  // 對於有視頻的卡片，直接渲染成 div（避免任何 React Native 組件的觸摸事件干擾）
  if (embedObj?.src || embedObj?.html) {
    // 確保 YouTube URL 有必要的參數
    let finalSrc = embedObj?.src || '';
    if (!embedObj?.html && finalSrc) {
      try {
        if (finalSrc.includes('youtube.com/embed/')) {
          const url = new URL(finalSrc);
          if (!url.searchParams.has('playsinline')) {
            url.searchParams.set('playsinline', '1');
          }
          if (!url.searchParams.has('enablejsapi')) {
            url.searchParams.set('enablejsapi', '1');
          }
          if (!url.searchParams.has('origin') && typeof window !== 'undefined') {
            url.searchParams.set('origin', window.location.origin);
          }
          finalSrc = url.toString();
        }
      } catch (e) {
        // URL parsing failed, use original
      }
    }
    
    // 視頻卡片：使用 TouchableOpacity 包裹，onPress 打開 modal 或展開/收起
    return (
      <TouchableOpacity
        activeOpacity={0.8}
        onPress={() => {
          // 優先打開 modal（如果有 onOpenPoi）
          if (typeof onOpenPoi === 'function') {
            const payloadForModal = mergePayload(it);
            onOpenPoi({ ...payloadForModal, _item: it });
          } else if (shouldShowToggle && typeof onToggleExpand === 'function') {
            // 否則展開/收起描述
            onToggleExpand();
          }
        }}
        style={{
          backgroundColor: '#fff',
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          border: '1px solid #E5E7EB',
          overflow: 'visible',
          marginBottom: 12,
          boxShadow: '0 4px 12px rgba(15,23,42,0.06)',
        }}
      >
        {/* Video iframe container - 完全不添加任何事件處理器 */}
        <div 
          data-video-player="true"
          style={{ 
            width: '100%', 
            position: 'relative', 
            paddingBottom: '56.25%' , 
            background: '#000',
          }}
        >
          {embedObj?.html ? (
             <iframe
                srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><style>body{margin:0;display:flex;justify-content:center;align-items:center;background:#000;}</style></head><body>${embedObj.html}</body></html>`}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none',
                }}
                allowFullScreen
                loading="lazy"
                title={cleanedTitle}
             />
          ) : (
            <iframe
              src={finalSrc}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              loading="lazy"
              title={cleanedTitle}
            />
          )}
        </div>
        
        {/* Card body - 使用純 HTML 避免事件捕獲問題 */}
        <div style={{ paddingTop: 16, paddingBottom: 16, paddingLeft: 16, paddingRight: 16 }}>
          <div style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8, display: 'flex' }}>
            <div style={{ flexDirection: 'row', alignItems: 'center', flex: 1, marginRight: 12, display: 'flex' }}>
              <div style={{ 
                width: 40, 
                height: 40, 
                borderRadius: 20, 
                alignItems: 'center', 
                justifyContent: 'center', 
                marginRight: 12,
                backgroundColor: '#F3F4F6', 
                borderWidth: 1, 
                borderStyle: 'solid',
                borderColor: '#E5E7EB',
                display: 'flex'
              }}> 
                {favicon ? (
                  <>
                    <img
                      src={favicon}
                      alt="favicon"
                      style={{ width: 24, height: 24, borderRadius: 12, objectFit: 'cover' }}
                      loading="lazy"
                      onError={(e) => {
                        console.error('[ExternalAdapter] favicon failed to load:', favicon);
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const parent = target.parentElement;
                        if (parent) {
                          const fallback = document.createElement('div');
                          fallback.innerHTML = '<span style="display:flex;align-items:center;justify-content:center;width:24px;height:24px;">🌐</span>';
                          parent.appendChild(fallback);
                        }
                      }}
                      onLoad={() => {
                        console.log('[ExternalAdapter] favicon loaded successfully:', favicon);
                      }}
                    />
                  </>
                ) : (
                  <WebIcon name="travel-explore" size={18} color="#FB7A4A" />
                )}
              </div>
              <div style={{ 
                flex: 1, 
                fontSize: 16, 
                fontWeight: 600, 
                color: '#111827', 
                lineHeight: '22px',
                display: '-webkit-box',
                WebkitLineClamp: isExpanded ? 'unset' : 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}>{cleanedTitle}</div>
            </div>
          </div>
          
          {/* Address row */}
          {address ? (
            <div style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: 8, display: 'flex' }}>
              <div style={{ marginRight: 8 }}>
                <WebIcon name="place" size={16} color="#9CA3AF" family="MaterialIcons" />
              </div>
              <div style={{ 
                fontSize: 13, 
                color: '#374151', 
                flex: 1, 
                lineHeight: '18px',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}>{address}</div>
            </div>
          ) : null}
          
          {/* Phone row */}
          {phone ? (
            <div style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8, display: 'flex' }}>
              <div style={{ marginRight: 8 }}>
                <WebIcon name="phone" size={16} color="#9CA3AF" family="MaterialIcons" />
              </div>
              <div style={{ fontSize: 13, color: '#374151' }}>{phone}</div>
            </div>
          ) : null}
          
          {/* Transport row */}
          {transportNote ? (
            <div style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: 8, display: 'flex' }}>
              <div style={{ marginRight: 8 }}>
                <WebIcon name="directions-transit" size={16} color="#9CA3AF" family="MaterialIcons" />
              </div>
              <div style={{ 
                fontSize: 13, 
                color: '#374151', 
                flex: 1, 
                lineHeight: '18px',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}>{transportNote}</div>
            </div>
          ) : null}
          
          {/* Description (note) */}
          {trimmedDescription ? (
            <div style={{ 
              fontSize: 14, 
              color: '#374151', 
              lineHeight: '20px', 
              marginBottom: 12,
              display: '-webkit-box',
              WebkitLineClamp: isExpanded ? 'unset' : 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}>{trimmedDescription}</div>
          ) : null}
          
          {/* Other metaPairs - REMOVED per user request */}
          {isDegraded ? (
            <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 8 }}>
              此網站限制了預覽資訊，請點擊下方按鈕查看完整內容。
            </div>
          ) : null}
          {shouldShowToggle ? (
            <div
              onClick={(event) => handleToggleExpand(event)}
              style={{
                marginTop: 12,
                paddingTop: 8,
                paddingBottom: 8,
                paddingLeft: 16,
                paddingRight: 16,
                backgroundColor: 'transparent',
                borderWidth: 1,
                borderStyle: 'solid',
                borderColor: '#E5E7EB',
                borderRadius: 8,
                alignSelf: 'center',
                fontSize: 13,
                fontWeight: 500,
                color: '#6B7280',
                cursor: 'pointer',
                border: '1px solid #E5E7EB',
                textAlign: 'center'
              }}
            >
              {isExpanded ? '收起 ▴' : '展开 ▾'}
            </div>
          ) : null}
        </div>
      </TouchableOpacity>
    );
  }

  // 對於其他卡片使用 TouchableOpacity
  return (
    <TouchableOpacity
      style={styles.richCardContainer}
      activeOpacity={finalWebsite ? 0.75 : 1}
      onPress={handleOpen}
    >
      {finalImageUrl ? (
        <div style={{ width: '100%', height: 160, position: 'relative', overflow: 'hidden', background: '#F3F4F6' }}>
          <img
            src={finalImageUrl}
            alt={cleanedTitle}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            loading="lazy"
          />
          {overlaySiteBadge ? (
            <div style={{ position: 'absolute', right: 12, bottom: 12 }}>
              {overlaySiteBadge}
            </div>
          ) : null}
        </div>
      ) : null}
      <View style={styles.cardBody}>
        <View style={styles.cardHeaderRow}>
          <View style={styles.cardTitleRow}>
            <View style={[styles.cardTypeIcon, { backgroundColor: '#F3F4F6', borderColor: '#E5E7EB' }]}> 
              {favicon ? (
                <>
                  <img
                    src={favicon}
                    alt="favicon"
                    style={{ width: 24, height: 24, borderRadius: 12, objectFit: 'cover' }}
                    loading="lazy"
                    onError={(e) => {
                      console.error('[ExternalAdapter] favicon failed to load:', favicon);
                      // Hide the img and show fallback icon
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        const fallback = document.createElement('div');
                        fallback.innerHTML = '<span style="display:flex;align-items:center;justify-content:center;width:24px;height:24px;">🌐</span>';
                        parent.appendChild(fallback);
                      }
                    }}
                    onLoad={() => {
                      console.log('[ExternalAdapter] favicon loaded successfully:', favicon);
                    }}
                  />
                </>
              ) : (
                <WebIcon name="travel-explore" size={18} color="#FB7A4A" />
              )}
            </View>
            <Text style={styles.cardTitle} numberOfLines={isExpanded ? undefined : 2}>{cleanedTitle}</Text>
          </View>
        </View>
        
        {/* Address row */}
        {address ? (
          <View style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: 8 }}>
            <View style={{ marginRight: 8 }}>
              <WebIcon name="place" size={16} color="#9CA3AF" family="MaterialIcons" />
            </View>
            <Text style={{ fontSize: 13, color: '#374151', flex: 1, lineHeight: 18 }} numberOfLines={2}>{address}</Text>
          </View>
        ) : null}
        
        {/* Phone row */}
        {phone ? (
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
            <View style={{ marginRight: 8 }}>
              <WebIcon name="phone" size={16} color="#9CA3AF" family="MaterialIcons" />
            </View>
            <Text style={{ fontSize: 13, color: '#374151' }}>{phone}</Text>
          </View>
        ) : null}
        
        {/* Transport row */}
        {transportNote ? (
          <View style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: 8 }}>
            <View style={{ marginRight: 8 }}>
              <WebIcon name="directions-transit" size={16} color="#9CA3AF" family="MaterialIcons" />
            </View>
            <Text style={{ fontSize: 13, color: '#374151', flex: 1, lineHeight: 18 }} numberOfLines={3}>{transportNote}</Text>
          </View>
        ) : null}
        
        {/* Description (note) */}
        {trimmedDescription ? (
          <Text style={styles.cardDescription} numberOfLines={isExpanded ? undefined : 2}>{trimmedDescription}</Text>
        ) : null}
        
        {/* Other metaPairs (excluding address, phone, transport) - REMOVED per user request */}
        {isDegraded ? (
          <Text style={{ fontSize: 12, color: '#6B7280', marginBottom: 8 }}>
            此網站限制了預覽資訊，請點擊下方按鈕查看完整內容。
          </Text>
        ) : null}
        {shouldShowToggle ? (
          <TouchableOpacity
            activeOpacity={0.7}
            onPress={(event) => handleToggleExpand(event)}
            style={styles.cardExpandToggle}
          >
            <Text style={styles.cardExpandToggleText}>{isExpanded ? '收起 ▴' : '展开 ▾'}</Text>
          </TouchableOpacity>
        ) : null}
      </View>
    </TouchableOpacity>
  );
};

export default ExternalAdapter;
